import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Check, X, Plus } from "lucide-react";
import AggiungiDialog from "./AggiungiDialog";
import { ResourceChip, DropZone } from "./ResourceChip";

const STATO = { standby: "STAND-BY", futuro: "FUTURO", chiuso: "CHIUSO" };

// Iter 106: colonna sede della lavagna (testata + note giornaliere + tessere)
export default function SedeColumn({ col, data, canEdit, onMove, onChanged, catalog = [] }) {
  const [addOpen, setAddOpen] = useState(false);
  const s = col.sede;
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState(col.note || "");
  const [alloggio, setAlloggio] = useState(col.alloggio || "");
  const [commesse, setCommesse] = useState((col.commesse || []).join(", "));
  const people = col.items.filter((i) => i.tipo === "interno" || i.tipo === "esterno");
  const mezzi = col.items.filter((i) => i.tipo === "mezzo");
  const rest = col.items.filter((i) => i.tipo === "mezzo_cantiere" || i.tipo === "attrezzatura");

  const saveNote = async () => {
    try {
      await api.put(`/programma/${data}/colonna`, { sede_id: s.id, note, alloggio, commesse: commesse.split(",").map((x) => x.trim()).filter(Boolean) });
      setEditing(false); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <DropZone canDrop={canEdit} onDrop={(p) => onMove(p, s.id)} className={`w-full min-w-0 rounded-xl border flex flex-col ${people.length === 0 ? "border-dashed border-slate-300 bg-slate-100/60 opacity-80" : "border-slate-200 bg-slate-50/70"}`} testId={`col-${s.id}`}>
      <div className="px-3 py-2 border-b border-slate-200 bg-white rounded-t-xl">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-heading font-extrabold text-sm text-slate-900 uppercase leading-tight">{s.nome}{s.pm && <span className="text-slate-500 font-semibold"> – PM {s.pm}</span>}</div>
            {s.indirizzo && <div className="text-[11px] text-slate-500 truncate">{s.indirizzo}</div>}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {people.length === 0 && <span className="rounded bg-slate-500 text-white px-1.5 py-0.5 text-[10px] font-bold" data-testid={`col-fermo-${s.id}`}>FERMO</span>}
            {STATO[s.stato] && <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-600">{STATO[s.stato]}</span>}
            <span className={`rounded-full text-white text-[10px] font-black px-1.5 py-0.5 ${people.length === 0 ? "bg-slate-400" : "bg-[#0044FF]"}`} data-testid={`col-count-${s.id}`}>{people.length}</span>
            {canEdit && <button type="button" onClick={() => setAddOpen(true)} title="Aggiungi persone, mezzi o attrezzature a questa sede" className="rounded-full border border-[#0044FF] text-[#0044FF] hover:bg-blue-50 p-0.5" data-testid={`col-add-${s.id}`}><Plus className="h-3.5 w-3.5" /></button>}
          </div>
        </div>
      </div>
      <div className="p-2 space-y-1 overflow-y-auto flex-1 min-h-[60px]">
        {people.map((i) => <ResourceChip key={`${i.tipo}-${i.id}`} item={i} canDrag={canEdit} />)}
        {mezzi.map((i) => <ResourceChip key={`${i.tipo}-${i.id}`} item={i} canDrag={canEdit} compact />)}
        {(note || alloggio || commesse || editing || canEdit) && (
          <div className="mt-2 rounded-md border border-dashed border-slate-300 bg-white px-2 py-1.5 text-[11px] text-slate-700">
            {editing ? (
              <div className="space-y-1">
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="NOTE" className="w-full rounded border px-1.5 py-1 text-[11px]" data-testid={`col-note-input-${s.id}`} />
                <input value={commesse} onChange={(e) => setCommesse(e.target.value)} placeholder="Comm: EG-.., EG-.." className="w-full rounded border px-1.5 py-1 text-[11px] font-mono" />
                <input value={alloggio} onChange={(e) => setAlloggio(e.target.value)} placeholder="Alloggio" className="w-full rounded border px-1.5 py-1 text-[11px]" />
                <div className="flex justify-end gap-1">
                  <button onClick={() => setEditing(false)} className="p-1 rounded hover:bg-slate-100"><X className="h-3.5 w-3.5" /></button>
                  <button onClick={saveNote} className="p-1 rounded bg-[#0044FF] text-white" data-testid={`col-note-save-${s.id}`}><Check className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-1">
                <div className="flex-1 min-w-0 space-y-0.5">
                  {note && <div><b>NOTE:</b> {note}</div>}
                  {commesse && <div className="font-mono"><b className="font-sans">Comm:</b> {commesse}</div>}
                  {alloggio && <div><b>Alloggio:</b> {alloggio}</div>}
                  {!note && !commesse && !alloggio && <span className="text-slate-400">Note, commesse, alloggio…</span>}
                </div>
                {canEdit && <button onClick={() => setEditing(true)} className="p-0.5 rounded hover:bg-slate-100 text-slate-500" data-testid={`col-note-edit-${s.id}`}><Pencil className="h-3 w-3" /></button>}
              </div>
            )}
          </div>
        )}
        {rest.length > 0 && <div className="pt-1 space-y-1 border-t border-slate-200 mt-2">{rest.map((i) => <ResourceChip key={`${i.tipo}-${i.id}`} item={i} canDrag={canEdit} compact />)}</div>}
        {canEdit && <button type="button" onClick={() => setAddOpen(true)} className="w-full mt-1 rounded-md border border-dashed border-[#0044FF]/50 text-[#0044FF] text-[11px] font-bold py-1 hover:bg-blue-50 inline-flex items-center justify-center gap-1" data-testid={`col-add-bottom-${s.id}`}><Plus className="h-3 w-3" /> Aggiungi</button>}
      </div>
      {addOpen && <AggiungiDialog open onClose={() => setAddOpen(false)} sede={s} data={data} catalog={catalog} onDone={onChanged} />}
    </DropZone>
  );
}
